import React, { useState, useEffect } from 'react';
import DealDetail from './DealDetail';
import StartDeal from './StartDeal';
import { authFetch } from '../auth/AuthContext';

const STAGES = ['New application', 'Review', 'Sent to lenders', 'Terms received', 'Processing', 'Funded', 'Closed lost'];

const FIRST_STAGE = 'New application';

const TONE = {
  'New application': 'info',
  'Review': 'info',
  'Sent to lenders': 'info',
  'Terms received': 'done',
  'Processing': 'info',
  'Funded': 'done',
  'Closed lost': 'waiting',
};

const PILL = {
  info: { background: '#F0F5FB', color: '#1A5FA8' },
  done: { background: '#EAF3DE', color: '#3B6D11' },
  needs: { background: '#FAEEDA', color: '#8a5a12' },
  waiting: { background: '#EEF1F5', color: '#8A9AB5' },
};

const DOT = { info: '#4A7FBF', done: '#3B6D11', needs: '#EF9F27', waiting: '#C9D9EE' };

function toneOf(st) { return TONE[st] || 'info'; }

function clean(str) {
  return String(str || '').replace(/\s*[\u2014\u2013\u2015\u2012\u2212]\s*/g, ' - ');
}

function pillStyle(st) {
  return { ...PILL[toneOf(st)], fontSize: '12px', fontWeight: 500, padding: '5px 12px', borderRadius: '20px', whiteSpace: 'nowrap', display: 'inline-block' };
}

// The stored deal name is one string, "Borrower - Street address".
// The Kanban card shows it on two lines with no hyphen; the list shows it
// on two lines as name over address. Display only, nothing is re-saved.
function splitName(f) {
  const name = clean(f?.['Deal name']);
  const addr = clean(f?.['Property address']);
  if (name && addr && name.indexOf(addr) !== -1) {
    const who = name.replace(addr, '').replace(/\s*-\s*$/, '').replace(/^\s*-\s*/, '').trim();
    return { who: who || name, addr };
  }
  const parts = name.split(' - ');
  if (parts.length > 1) return { who: parts[0].trim(), addr: parts.slice(1).join(' - ').trim() };
  return { who: name, addr: addr };
}

function cityState(f) {
  return [f?.['Property city'], f?.['Property state']].filter(Boolean).join(', ');
}

function fullAddress(f) {
  const cs = cityState(f);
  const zip = f?.['Property ZIP'];
  return clean([f?.['Property address'], cs, zip].filter(Boolean).join(', '));
}

// Days in the current stage, read from the "Stage changed" last-modified-time
// field in Airtable. Nothing writes to it, so it cannot drift.
function daysInStage(f) {
  const raw = f?.['Stage changed'];
  if (!raw) return null;
  const then = new Date(raw);
  if (isNaN(then.getTime())) return null;
  const days = Math.floor((Date.now() - then.getTime()) / 86400000);
  return days < 0 ? 0 : days;
}

function formatMoney(v) {
  const n = Number(v);
  if (!n) return '';
  if (n >= 1000000) return '$' + (Math.round((n / 1000000) * 100) / 100) + 'M';
  if (n >= 1000) return '$' + Math.round(n / 1000) + 'K';
  return '$' + Math.round(n).toLocaleString();
}

const POP = "'Inter',sans-serif";

const s = {
  h1: { fontFamily: POP, fontSize: '20px', fontWeight: 600, letterSpacing: '-0.005em', margin: '0 0 16px', color: '#2E4763' },

  dealsCard: { background: '#fff', border: '1px solid #DCE7F5', borderRadius: '12px', overflow: 'hidden' },
  dealsBar: { background: '#E4EDF8', padding: '11px 16px', display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap' },
  dealsBarLbl: { fontFamily: POP, fontSize: '12px', fontWeight: 700, color: '#154D97', letterSpacing: '0.07em', textTransform: 'uppercase' },
  chip: { background: '#fff', borderRadius: '20px', fontSize: '11.5px', color: '#3A6BA8', padding: '3px 11px' },
  dealsBarCtl: { marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' },
  toggle: { display: 'flex', background: '#fff', border: '1px solid #DCE7F5', borderRadius: '8px', overflow: 'hidden' },
  tBtn: (a) => ({ display: 'flex', alignItems: 'center', gap: '6px', padding: '6px 14px', background: a ? '#F0F5FB' : '#fff', color: a ? '#154D97' : '#8A94A6', border: 'none', cursor: 'pointer', fontSize: '12.5px', fontWeight: a ? 600 : 500, fontFamily: 'inherit' }),
  dealsBody: { padding: '16px' },

  searchRow: { display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '16px', flexWrap: 'wrap' },
  search: { width: '260px', maxWidth: '100%', background: '#fff', border: '1px solid #DCE7F5', borderRadius: '8px', padding: '8px 12px', fontSize: '13px', color: '#0D2545', outline: 'none', fontFamily: 'inherit' },

  summary: { background: '#F0F5FB', borderRadius: '10px', padding: '13px 18px', display: 'flex', gap: '34px', flexWrap: 'wrap', marginBottom: '16px' },
  mLabel: { fontSize: '10px', fontWeight: 700, letterSpacing: '0.07em', textTransform: 'uppercase', color: '#8A94A6', marginBottom: '3px' },
  mValue: { fontFamily: POP, fontSize: '19px', fontWeight: 600, color: '#154D97' },

  loading: { padding: '40px', textAlign: 'center', color: '#9aa8bd', fontSize: '13.5px' },
  emptyList: { fontSize: '13.5px', color: '#9aa8bd', padding: '24px 0', textAlign: 'center' },

  foot: { display: 'flex', alignItems: 'center', marginTop: '14px', gap: '10px', flexWrap: 'wrap' },
  addBtn: { border: '1px dashed #7EA3D7', background: '#fff', color: '#154D97', borderRadius: '22px', fontSize: '13px', fontWeight: 600, padding: '8px 18px', cursor: 'pointer', fontFamily: 'inherit' },
  total: { marginLeft: 'auto', fontFamily: POP, fontSize: '13px', fontWeight: 600, color: '#154D97' },
  note: { fontSize: '11.5px', color: '#8A94A6', fontStyle: 'italic', marginTop: '12px' },

  lHead: { display: 'flex', alignItems: 'center', gap: '20px', padding: '0 14px 9px', fontSize: '10px', fontWeight: 700, letterSpacing: '0.07em', textTransform: 'uppercase', color: '#8A94A6' },
  lRow: { display: 'flex', alignItems: 'center', gap: '20px', background: '#fff', border: '1px solid #DCE7F5', borderRadius: '10px', padding: '13px 14px', marginBottom: '8px', cursor: 'pointer', transition: 'border-color 0.12s' },
  cExp: { width: '14px', flex: 'none', color: '#C2CCDA', fontSize: '10px' },
  cDeal: { flex: 1, minWidth: 0 },
  cType: { width: '112px', flex: 'none' },
  cEntity: { width: '148px', flex: 'none' },
  cDays: { width: '54px', flex: 'none', textAlign: 'right' },
  cAmt: { width: '84px', flex: 'none', textAlign: 'right' },
  cStage: { width: '136px', flex: 'none' },
  cTrash: { width: '28px', flex: 'none', textAlign: 'right' },
  rowName: { fontSize: '13.5px', fontWeight: 600, color: '#0D2545' },
  rowAddr: { fontSize: '12.5px', color: '#3A6BA8', marginTop: '3px', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' },
  cell: { fontSize: '12.5px', color: '#3A6BA8', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' },
  cellNone: { fontSize: '12.5px', color: '#C2CCDA' },
  amt: { fontFamily: POP, fontSize: '13.5px', fontWeight: 600, color: '#0D2545', whiteSpace: 'nowrap' },
  trashBtn: { border: 'none', background: 'transparent', color: '#C2CCDA', cursor: 'pointer', padding: '4px', borderRadius: '7px', display: 'inline-flex', alignItems: 'center', fontSize: '15px', transition: 'color 0.12s, background 0.12s' },

  board: { display: 'flex', gap: '12px', alignItems: 'flex-start', overflowX: 'auto', paddingBottom: '8px' },
  col: { width: '186px', flex: 'none' },
  colHead: { display: 'flex', alignItems: 'center', gap: '6px', padding: '0 2px 8px', borderBottom: '2px solid #DCE7F5' },
  colHeadLive: { display: 'flex', alignItems: 'center', gap: '6px', padding: '0 2px 8px', borderBottom: '2px solid #4A7FBF' },
  colName: { fontFamily: POP, fontSize: '10px', fontWeight: 700, letterSpacing: '0.07em', textTransform: 'uppercase', color: '#8A94A6', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' },
  colNameLive: { fontFamily: POP, fontSize: '10px', fontWeight: 700, letterSpacing: '0.07em', textTransform: 'uppercase', color: '#154D97', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' },
  colNum: { marginLeft: 'auto', fontSize: '11px', color: '#C2CCDA' },
  colNumLive: { marginLeft: 'auto', fontSize: '11px', color: '#8A94A6' },
  colCards: { paddingTop: '10px', display: 'flex', flexDirection: 'column', gap: '8px', minHeight: '40px' },
  kEmpty: { paddingTop: '11px', fontSize: '11.5px', color: '#C2CCDA' },

  kCard: { background: '#fff', border: '1px solid #DCE7F5', borderRadius: '10px', padding: '11px 12px', cursor: 'grab', transition: 'border-color 0.12s' },
  kTop: { display: 'flex', gap: '8px', alignItems: 'flex-start' },
  kName: { fontSize: '13px', fontWeight: 600, color: '#0D2545', lineHeight: 1.35, minWidth: 0 },
  kLine: { fontSize: '12px', color: '#3A6BA8', marginTop: '3px', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' },
  kSub: { fontSize: '12px', color: '#5F6B7F', marginTop: '3px', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' },
  kFoot: { display: 'flex', alignItems: 'center', marginTop: '9px', paddingTop: '9px', borderTop: '1px solid #EEF3FA' },
  kAmt: { fontFamily: POP, fontSize: '12.5px', fontWeight: 600, color: '#0D2545' },
  kTrash: { border: 'none', background: 'transparent', color: '#C2CCDA', cursor: 'pointer', padding: '2px', borderRadius: '6px', display: 'flex', alignItems: 'center', fontSize: '14px', lineHeight: 1, flexShrink: 0, marginLeft: 'auto', transition: 'color 0.12s, background 0.12s' },

  days: { fontSize: '11.5px', color: '#5F6B7F', whiteSpace: 'nowrap' },
  daysStale: { fontSize: '11.5px', color: '#8A5A12', background: '#FAEEDA', borderRadius: '20px', padding: '2px 8px', whiteSpace: 'nowrap' },
};

function dayStyle(n) { return n != null && n >= 30 ? s.daysStale : s.days; }
function dayText(n) { return n == null ? '' : n + 'd'; }

export default function Deals({ startNew } = {}) {
  const [deals, setDeals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [selected, setSelected] = useState(null);
  const [starting, setStarting] = useState(!!startNew);
  const [view, setView] = useState('kanban');

  // The deal list was fetched once and never again, so anything saved inside a
  // deal was invisible when that deal was reopened in the same session: the
  // record handed to DealDetail was still the copy loaded at sign-in. Reload
  // the list on the way back out of a deal so reopening shows what was saved.
  function loadDeals() {
    authFetch('/.netlify/functions/get-deals').then(r => r.json()).then(d => { setDeals(d.records || []); setLoading(false); }).catch(() => setLoading(false));
  }
  useEffect(() => {
    loadDeals();
  }, []); // eslint-disable-line


  async function moveDeal(dealId, newStage) {
    const target = deals.find(d => d.id === dealId);
    if (!target || target.fields?.Stage === newStage) return;
    const prevStage = target.fields?.Stage;
    setDeals(prev => prev.map(d => d.id === dealId ? { ...d, fields: { ...d.fields, Stage: newStage } } : d));
    try {
      const r = await authFetch('/.netlify/functions/update-deal', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ dealId, fields: { Stage: newStage } }) });
      const data = await r.json();
      if (data.error) throw new Error(data.error?.message || 'save failed');
    } catch (e) {
      setDeals(prev => prev.map(d => d.id === dealId ? { ...d, fields: { ...d.fields, Stage: prevStage } } : d));
    }
  }

  async function deleteDeal(dealId) {
    const target = deals.find(d => d.id === dealId);
    if (!target) return;
    const name = clean(target.fields?.['Deal name']) || 'this deal';
    if (!window.confirm('Delete ' + name + '?\n\nThis removes the deal, every income, asset, liability, property and document row on it, and moves its Drive folder to the trash.\n\nContacts are not deleted. Drive keeps the folder in the trash for 30 days.')) return;
    const prev = deals;
    setDeals(p => p.filter(d => d.id !== dealId));
    try {
      const r = await authFetch('/.netlify/functions/delete-deal-full', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ dealId }) });
      const data = await r.json();
      if (!r.ok || data.error) throw new Error(data.error || 'delete failed');
      if (Array.isArray(data.warnings) && data.warnings.length) {
        window.alert('Deal deleted, but some parts did not clean up:\n\n' + data.warnings.join('\n'));
      }
    } catch (e) {
      setDeals(prev);
      window.alert('Could not delete the deal: ' + (e.message || 'please try again.'));
    }
  }

  if (selected) return <DealDetail deal={selected} onBack={() => { setSelected(null); setStarting(false); loadDeals(); }} onCreated={() => setStarting(false)} />;
  if (starting) return <StartDeal onCancel={() => setStarting(false)} onBlank={() => { setStarting(false); setSelected({ id: null, name: 'New deal', fields: {} }); }} />;

  const filtered = deals.filter(d => {
    const name = (d.fields?.['Deal name'] || '').toLowerCase();
    const addr = (d.fields?.['Property address'] || '').toLowerCase();
    const q = search.toLowerCase();
    return name.includes(q) || addr.includes(q);
  });

  const volume = filtered.reduce((sum, d) => sum + (Number(d.fields?.['Loan amount']) || 0), 0);
  const funded = filtered.filter(d => d.fields?.Stage === 'Funded').length;
  const lost = filtered.filter(d => d.fields?.Stage === 'Closed lost').length;
  const active = filtered.length - funded - lost;
  const stale = filtered.filter(d => { const n = daysInStage(d.fields || {}); return n != null && n >= 30; }).length;

  return (
    <div>
      <div style={s.dealsCard}>
        <div style={s.dealsBar}>
          <span style={s.dealsBarLbl}>Deals</span>
          <span style={s.chip}>{filtered.length} deal{filtered.length !== 1 ? 's' : ''}</span>
          <div style={s.dealsBarCtl}>
            <div style={s.toggle}>
              <button style={s.tBtn(view === 'kanban')} onClick={() => setView('kanban')}>Kanban</button>
              <button style={s.tBtn(view === 'list')} onClick={() => setView('list')}>List</button>
            </div>
          </div>
        </div>

        <div style={s.dealsBody}>
          <div style={s.searchRow}>
            <input style={s.search} placeholder="Search deals..." value={search} onChange={e => setSearch(e.target.value)} />
          </div>

          <div style={s.summary}>
            <div>
              <div style={s.mLabel}>Total volume</div>
              <div style={s.mValue}>{formatMoney(volume) || '$0'}</div>
            </div>
            <div>
              <div style={s.mLabel}>Active</div>
              <div style={s.mValue}>{active}</div>
            </div>
            <div>
              <div style={s.mLabel}>Funded</div>
              <div style={s.mValue}>{funded}</div>
            </div>
            <div>
              <div style={s.mLabel}>Over 30 days</div>
              <div style={s.mValue}>{stale}</div>
            </div>
          </div>

          {loading ? <div style={s.loading}>Loading deals</div>
            : view === 'kanban' ? <Kanban deals={filtered} onSelect={setSelected} onMove={moveDeal} onDelete={deleteDeal} />
              : <List deals={filtered} onSelect={setSelected} onDelete={deleteDeal} />}

          {!loading && (
            <div style={s.foot}>
              <button style={s.addBtn} onClick={() => setStarting(true)}>+ Deal</button>
              <div style={s.total}>Total volume {formatMoney(volume) || '$0'}</div>
            </div>
          )}

          {!loading && <div style={s.note}>Days shown are days in the current stage. Amber past thirty days.</div>}
        </div>
      </div>
    </div>
  );
}

function List({ deals, onSelect, onDelete }) {
  if (deals.length === 0) return <div style={s.emptyList}>No deals found.</div>;
  return (
    <div>
      <div style={s.lHead}>
        <div style={s.cExp} />
        <div style={s.cDeal}>Deal</div>
        <div style={s.cType}>Loan type</div>
        <div style={s.cEntity}>Company / entity</div>
        <div style={s.cDays}>Days</div>
        <div style={s.cAmt}>Amount</div>
        <div style={s.cStage}>Stage</div>
        <div style={s.cTrash} />
      </div>
      {deals.map(d => {
        const f = d.fields || {};
        const { who } = splitName(f);
        const amt = formatMoney(f['Loan amount']);
        const st = f.Stage || FIRST_STAGE;
        const ent = clean(f['Entity name']);
        const n = daysInStage(f);
        return (
          <div key={d.id} style={s.lRow} onClick={() => onSelect(d)}
            onMouseEnter={e => { e.currentTarget.style.borderColor = '#7EA3D7'; }}
            onMouseLeave={e => { e.currentTarget.style.borderColor = '#DCE7F5'; }}>
            <div style={s.cExp}>&#9654;</div>
            <div style={s.cDeal}>
              <div style={s.rowName}>{who || 'Unnamed deal'}</div>
              <div style={s.rowAddr}>{fullAddress(f)}</div>
            </div>
            <div style={{ ...s.cType, ...s.cell }}>{clean(f['Loan type'])}</div>
            <div style={{ ...s.cEntity, ...(ent ? s.cell : s.cellNone) }}>{ent || '-'}</div>
            <div style={s.cDays}>{n != null && <span style={dayStyle(n)}>{dayText(n)}</span>}</div>
            <div style={{ ...s.cAmt, ...s.amt }}>{amt}</div>
            <div style={s.cStage}><span style={pillStyle(st)}>{st}</span></div>
            <div style={s.cTrash}>
              <button style={s.trashBtn} title="Delete deal" onClick={e => { e.stopPropagation(); onDelete(d.id); }}
                onMouseEnter={e => { e.currentTarget.style.color = '#C0564B'; e.currentTarget.style.background = '#FBECEA'; }}
                onMouseLeave={e => { e.currentTarget.style.color = '#C2CCDA'; e.currentTarget.style.background = 'transparent'; }}>
                <i className="ti ti-trash" />
              </button>
            </div>
          </div>
        );
      })}
    </div>
  );
}

function Kanban({ deals, onSelect, onMove, onDelete }) {
  const [overStage, setOverStage] = useState(null);
  const known = new Set(STAGES);
  const bucketed = {};
  STAGES.forEach(st => { bucketed[st] = []; });
  const unsorted = [];
  deals.forEach(d => {
    const st = d.fields?.Stage;
    if (!st) bucketed[FIRST_STAGE].push(d);
    else if (known.has(st)) bucketed[st].push(d);
    else unsorted.push(d);
  });

  const columns = STAGES.map(st => ({ st, items: bucketed[st], unsorted: false }));
  if (unsorted.length) columns.unshift({ st: 'Unsorted', items: unsorted, unsorted: true });

  return (
    <div style={s.board}>
      {columns.map(({ st, items, unsorted: isUn }) => {
        const live = items.length > 0;
        const dropProps = isUn ? {} : {
          onDragOver: e => { e.preventDefault(); if (overStage !== st) setOverStage(st); },
          onDragLeave: () => setOverStage(cur => (cur === st ? null : cur)),
          onDrop: e => { e.preventDefault(); const id = e.dataTransfer.getData('text/plain'); setOverStage(null); if (id) onMove(id, st); },
        };
        const headStyle = isUn
          ? { ...s.colHead, borderBottomColor: '#EF9F27' }
          : (live ? s.colHeadLive : s.colHead);
        const nameStyle = isUn
          ? { ...s.colName, color: '#8A5A12' }
          : (live ? s.colNameLive : s.colName);
        const cardsStyle = (!isUn && overStage === st)
          ? { ...s.colCards, background: '#F0F5FB', borderRadius: '10px' }
          : s.colCards;
        return (
          <div key={st} style={s.col}>
            <div style={headStyle}>
              <span style={nameStyle}>{st}</span>
              <span style={live ? s.colNumLive : s.colNum}>{items.length}</span>
            </div>
            {items.length === 0 ? (
              <div style={s.kEmpty} {...dropProps}>Empty</div>
            ) : (
              <div style={cardsStyle} {...dropProps}>
                {items.map(d => {
                  const f = d.fields || {};
                  const { who, addr } = splitName(f);
                  const amt = formatMoney(f['Loan amount']);
                  const cs = cityState(f);
                  const lt = clean(f['Loan type']);
                  const ent = clean(f['Entity name']);
                  const n = daysInStage(f);
                  return (
                    <div key={d.id} style={s.kCard} draggable onDragStart={e => { e.dataTransfer.setData('text/plain', d.id); e.dataTransfer.effectAllowed = 'move'; }} onClick={() => onSelect(d)}
                      onMouseEnter={e => { e.currentTarget.style.borderColor = '#7EA3D7'; }}
                      onMouseLeave={e => { e.currentTarget.style.borderColor = '#DCE7F5'; }}>
                      <div style={s.kTop}>
                        <div style={s.kName}>{who || 'Unnamed'}</div>
                        <button style={s.kTrash} title="Delete deal" onClick={e => { e.stopPropagation(); onDelete(d.id); }}
                          onMouseEnter={e => { e.currentTarget.style.color = '#C0564B'; e.currentTarget.style.background = '#FBECEA'; }}
                          onMouseLeave={e => { e.currentTarget.style.color = '#C2CCDA'; e.currentTarget.style.background = 'transparent'; }}>
                          <i className="ti ti-trash" />
                        </button>
                      </div>
                      {addr && <div style={s.kLine}>{addr}</div>}
                      {cs && <div style={s.kSub}>{cs}</div>}
                      {lt && <div style={s.kSub}>{lt}</div>}
                      {ent && <div style={s.kSub}>{ent}</div>}
                      <div style={s.kFoot}>
                        <span style={s.kAmt}>{amt}</span>
                        {n != null && <span style={{ ...dayStyle(n), marginLeft: 'auto' }}>{dayText(n)}</span>}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}
